/**
 * Types for Budget Admin feature.
 * Used by Admin pages for budget management.
 */

// ============================================================================
// User Budget Types
// ============================================================================

/**
 * User budget info from GET /api/admin/budget/users
 */
export interface UserBudgetInfo {
  /** Azure AD Object ID */
  oid: string
  /** User's email address */
  email: string
  /** User's display name */
  displayName: string
  /** Assigned role code */
  roleCode: string
  /** Effective budget limit in USD (null for unlimited) */
  effectiveBudgetUsd: number | null
  /** Source of the budget: 'user-override' | 'role-default' | 'unlimited' */
  budgetSource: string | null
  /** Current month usage in USD */
  currentMonthUsageUsd: number
  /** Percentage of budget used */
  percentUsed: number
  /** Whether user has a budget override */
  hasOverride: boolean
  /** Reason for override (if any) */
  overrideReason: string | null
}

/**
 * Response from GET /api/admin/budget/users
 */
export interface ListUserBudgetsResponse {
  items: UserBudgetInfo[]
  totalCount: number
  page: number
  pageSize: number
}

/**
 * Request params for GET /api/admin/budget/users
 */
export interface ListUserBudgetsRequest {
  page?: number
  pageSize?: number
}

// ============================================================================
// Role Budget Types
// ============================================================================

/**
 * Role budget info from GET /api/admin/budget/roles
 */
export interface BudgetRoleInfo {
  /** Role ID */
  id: string
  /** Role display name */
  displayName: string
  /** Monthly budget in USD (null for unlimited) */
  monthlyBudgetUsd: number | null
}

/**
 * Request body for PUT /api/admin/budget/roles/{roleId}
 */
export interface UpdateRoleBudgetRequest {
  /** New monthly budget in USD (null for unlimited) */
  monthlyBudgetUsd: number | null
}

// ============================================================================
// User Budget Override Types
// ============================================================================

/**
 * Request body for PUT /api/admin/budget/users/{oid}/override
 */
export interface SetUserBudgetOverrideRequest {
  /** Override budget in USD */
  budgetUsd: number
  /** Reason for the override */
  reason?: string
}

// ============================================================================
// Budget Status Helpers
// ============================================================================

/**
 * Get status color based on percent used.
 */
export function getBudgetStatusColor(percentUsed: number, isUnlimited: boolean): string {
  if (isUnlimited) return 'text-gray-500'
  if (percentUsed >= 80) return 'text-red-600'
  if (percentUsed >= 60) return 'text-yellow-600'
  return 'text-green-600'
}

/**
 * Format USD amount for display.
 */
export function formatBudgetUsd(amount: number | null): string {
  if (amount === null) return 'Unlimited'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}
